import { Component, Input, Output, EventEmitter } from '@angular/core';
import { IoService } from '../../../services/io/io.service';

@Component({
  selector: 'app-source-select',
  templateUrl: './source-select.component.html',
  styleUrls: ['./source-select.component.scss']
})
export class SourceSelectComponent {

  @Input() viewKey: string = '';
  @Input() path: string = '';
  @Input() locationId: string = '';
  @Input() documentId: string = '';
  @Input() documentValues: any = {};
  @Input() displayValue: string = '';
  @Input() excludeValues: string[] = [];
  // Bulk edit: drop $-ref source filters that can't be resolved from documentValues
  // (various across the list) instead of returning no options.
  @Input() ignoreUnresolvedRefs: boolean = false;

  @Output() selectionChange = new EventEmitter<{ value: string | null, display: string }>();

  searchText: string = '';
  options: any[] = null;
  // Options the server had beyond the ones returned (see the footer row)
  moreCount = 0;
  selectedIndex: number = null;
  isOpen = false;
  debounceTime = 300;
  debounceTimeout: any;

  constructor(
    private ioService: IoService
  ) { }

  onFocus() {
    this.searchText = '';
    this.isOpen = true;
    this.loadOptions();
  }

  onBlur() {
    setTimeout(() => {
      this.isOpen = false;
      this.options = null;
      this.selectedIndex = null;
    }, 200);
  }

  onSearchChange() {
    clearTimeout(this.debounceTimeout);
    this.debounceTimeout = setTimeout(() => {
      this.selectedIndex = null;
      this.loadOptions();
    }, this.debounceTime);
  }

  async loadOptions() {
    const body: any = {
      viewKey: this.viewKey,
      path: this.path,
    };
    if (this.locationId) {
      body.locationId = this.locationId;
    }
    if (this.documentId) {
      body.documentId = this.documentId;
    }
    if (this.documentValues) {
      body.documentValues = this.documentValues;
    }
    if (this.ignoreUnresolvedRefs) {
      body.ignoreUnresolvedRefs = true;
    }
    if (this.searchText) {
      body.search = this.searchText;
    }
    if (this.excludeValues.length) {
      // Excluded server-side so already-picked values do not eat the option cap
      body.exclude = this.excludeValues;
    }

    try {
      const result: any = await this.ioService.post('/view/getSourceCollectionOptions', body);
      this.options = this.excludeValues.length
        ? result.options.filter(o => !this.excludeValues.includes(o.value))
        : result.options;
      const total = typeof result.total === 'number' ? result.total : this.options.length;
      this.moreCount = Math.max(0, total - this.options.length);
    } catch (e) {
      this.options = [];
      this.moreCount = 0;
    }
  }

  selectOption(option: any) {
    this.displayValue = option.display;
    this.isOpen = false;
    this.options = null;
    this.selectedIndex = null;
    this.selectionChange.emit({ value: option.value, display: option.display });
  }

  clearSelection(event?: MouseEvent) {
    if (event) event.preventDefault();
    this.displayValue = '';
    this.searchText = '';
    this.isOpen = false;
    this.options = null;
    this.selectedIndex = null;
    this.selectionChange.emit({ value: null, display: '' });
  }

  onKeyDown(event: KeyboardEvent) {
    if (!this.options || this.options.length === 0) return;

    switch (event.key) {
      case 'ArrowDown':
        event.preventDefault();
        this.selectedIndex = (this.selectedIndex === null || this.selectedIndex === this.options.length - 1)
          ? 0 : this.selectedIndex + 1;
        break;
      case 'ArrowUp':
        event.preventDefault();
        this.selectedIndex = (this.selectedIndex === null || this.selectedIndex === 0)
          ? this.options.length - 1 : this.selectedIndex - 1;
        break;
      case 'Enter':
        if (this.selectedIndex !== null) {
          this.selectOption(this.options[this.selectedIndex]);
        }
        break;
      case 'Escape':
        this.isOpen = false;
        this.options = null;
        this.selectedIndex = null;
        break;
    }
  }

}
